import { useState, useEffect, useCallback } from 'react';
import { initializePaddle, Paddle, CheckoutEventNames } from '@paddle/paddle-js';
import type { BillingInterval } from '@reqshare/shared';

const PADDLE_CLIENT_TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN ?? '';
const PADDLE_ENVIRONMENT = import.meta.env.VITE_PADDLE_ENVIRONMENT ?? 'sandbox';
const PADDLE_PRO_MONTHLY_PRICE_ID = import.meta.env.VITE_PADDLE_PRO_PRICE_ID ?? '';
const PADDLE_PRO_YEARLY_PRICE_ID = import.meta.env.VITE_PADDLE_PRO_YEARLY_PRICE_ID ?? '';

function isConfiguredPriceId(value: string): boolean {
  return Boolean(value) && !value.startsWith('<REPLACE_WITH_');
}

export function usePaddle(onCheckoutComplete?: () => void) {
  const [paddle, setPaddle] = useState<Paddle>();

  useEffect(() => {
    if (!PADDLE_CLIENT_TOKEN) return;

    initializePaddle({
      token: PADDLE_CLIENT_TOKEN,
      environment: PADDLE_ENVIRONMENT as 'sandbox' | 'production',
      eventCallback: (event) => {
        if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
          onCheckoutComplete?.();
        }
      },
    }).then((instance) => {
      if (instance) setPaddle(instance);
    });
  }, [onCheckoutComplete]);

  const openCheckout = useCallback(
    (userId: string, email?: string, billingInterval: BillingInterval = 'monthly') => {
      if (!paddle) return;

      const priceId = billingInterval === 'yearly'
        ? PADDLE_PRO_YEARLY_PRICE_ID
        : PADDLE_PRO_MONTHLY_PRICE_ID;

      if (!isConfiguredPriceId(priceId)) {
        console.error(`Missing Paddle ${billingInterval} price ID`);
        return;
      }

      paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customer: email ? { email } : undefined,
        customData: { userId },
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          successUrl: window.location.origin + '/subscription/success',
        },
      });
    },
    [paddle],
  );

  return { paddle, openCheckout, isReady: !!paddle };
}
